<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Icon from '@/components/Icon.vue';
import MenuList, { type NavItem } from '@/components/MenuList.vue';

defineProps<{
    navItems: NavItem[];
    height?: string | number;
    logo?: string;
}>();

/** Reactive breakpoint (sm: 640px). We use it ONLY to toggle visibility, not structure. */
const isMobile = ref(false);

if (typeof window !== 'undefined') {
    const mq = window.matchMedia('(max-width: 639.98px)');
    const update = () => (isMobile.value = mq.matches);
    onMounted(() => {
        update();
        mq.addEventListener('change', update);
    });
    onBeforeUnmount(() => mq.removeEventListener('change', update));
}

const open = ref(false);
</script>

<template>
    <header
        class="rounded-[40px] bg-gradient-to-l from-[#90A5C4] to-[#454F5E] w-[90%] relative"
        :style="{ height: typeof height === 'number' ? `${height}px` : height || '50px' }"
    >
        <div class="h-full flex justify-between gap-3 items-center pr-4 md:pr-0">
            <a href="/" class="h-full flex items-center" aria-label="EiCourts Home">
                <img v-if="logo" :src="logo" alt="EiCourts Logo" class="h-9/10" />
            </a>

            <!-- Mobile hamburger (always in DOM; visibility toggled) -->
            <button
                class="bg-transparent border-0 cursor-pointer text-white p-2 -m-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                @click="open = !open"
                :aria-expanded="open"
                aria-controls="mobile-nav"
                v-show="isMobile"
            >
                <Icon name="menu" class="text-3xl sm:text-4xl" />
            </button>

            <!-- Desktop nav + socials (always in DOM; visibility toggled) -->
            <div
                class="flex w-full justify-start"
                role="menubar"
                aria-label="Main navigation"
                v-show="!isMobile"
            >
                <MenuList :items="navItems" :depth="0" :mobile="false" />
            </div>

            <div
                class="h-full flex justify-center items-center gap-2 text-white text-3xl px-4"
                aria-label="Social links"
                v-show="!isMobile"
            >
                <Icon name="facebook-alt" class="cursor-pointer hover:scale-110" />
                <Icon name="instagram" class="cursor-pointer hover:scale-110" />
            </div>
        </div>

        <!-- Mobile nav panel — this one can stay conditional since open defaults false -->
        <Transition name="nav">
            <div
                v-if="isMobile && open"
                id="mobile-nav"
                class="absolute top-16 left-1/2 -translate-x-1/2 w-[95%] text-white text-lg py-3 px-4 bg-zinc-800/80 backdrop-blur-[12px] rounded-xl"
                role="dialog"
                aria-modal="false"
            >
                <MenuList :items="navItems" :depth="0" :mobile="true" />
            </div>
        </Transition>
    </header>
</template>

<style scoped>
.nav-enter-active,
.nav-leave-active {
    transition: all 0.2s ease;
}
.nav-enter-from,
.nav-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
.nav-enter-to,
.nav-leave-from {
    opacity: 1;
    transform: translateY(0);
}
</style>
